import type { Forum } from "@hp/types";

/** Indian-numbering currency formatting: crores above 1,00,00,000, lakhs below. */
function unitAndDivisor(amount: number): { unit: string; div: number } {
  if (Math.abs(amount) >= 10000000) return { unit: "Cr", div: 10000000 };
  return { unit: "L", div: 100000 };
}

function trimmed(value: number): string {
  if (value >= 100) return value.toFixed(0);
  if (value >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

export function formatINR(amount: number): string {
  const { unit, div } = unitAndDivisor(amount);
  return `₹${trimmed(amount / div)} ${unit}`;
}

/** Exposure rows may legitimately lack a quantified amount in the corpus. */
export function formatExposureINR(amount: number): string {
  if (!amount || amount <= 0) return "Not quantified";
  return formatINR(amount);
}

/** Formats a value_low/value_high (or amount_claimed/amount_allowed) pair sharing one unit. */
export function formatINRRange(low: number, high: number): string {
  const { unit, div } = unitAndDivisor(Math.max(Math.abs(low), Math.abs(high)));
  const lowStr = trimmed(low / div);
  const highStr = trimmed(high / div);
  if (lowStr === highStr) return `₹${lowStr} ${unit}`;
  return `₹${lowStr}–${highStr} ${unit}`;
}

export function formatCount(n: number): string {
  return n.toLocaleString("en-IN");
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${formatDate(iso)}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
}

export function formatIssueTag(tag: string): string {
  const words = tag.split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const FORUM_LABELS: Record<string, string> = {
  central_tribunal: "Central Tribunal",
  appellate_tribunal: "Appellate Tribunal",
  supreme_court: "Supreme Court",
  state_commission: "State Commission",
  central_ministry: "Ministry of Power",
};

export function formatForum(forum: Forum | string): string {
  return FORUM_LABELS[forum] ?? formatIssueTag(forum);
}

export const CLAIM_STATUS_LABELS: Record<string, string> = {
  identified: "Identified",
  validated: "Validated",
  filed: "Filed",
  allowed: "Allowed",
  recovered: "Recovered",
  rejected: "Rejected",
  deferred: "Deferred",
};
